/**
 * Restaurant cart — kept entirely client-side (localStorage) so it cannot
 * collide with the server-side grocery cart. One open cart at a time, scoped
 * to a single restaurant (Swiggy/Zomato style: switching restaurants prompts
 * the user to clear).
 *
 * Plug-point: when checkout is implemented for restaurants, the items here
 * map cleanly onto an OrderCreate payload.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface RestaurantCartLine {
  menu_item_id: number;
  name: string;
  price: number;
  image_url?: string | null;
  quantity: number;
}

interface RestaurantCartState {
  restaurantId: number | null;
  restaurantName: string | null;
  lines: RestaurantCartLine[];
  addLine: (
    restaurantId: number,
    restaurantName: string,
    line: Omit<RestaurantCartLine, 'quantity'>,
    qty?: number,
  ) => { ok: boolean; conflict?: boolean };
  setQuantity: (menuItemId: number, qty: number) => void;
  remove: (menuItemId: number) => void;
  clear: () => void;
  total: () => number;
  itemCount: () => number;
}

export const useRestaurantCart = create<RestaurantCartState>()(
  persist(
    (set, get) => ({
      restaurantId: null,
      restaurantName: null,
      lines: [],
      addLine: (restaurantId, restaurantName, line, qty = 1) => {
        const state = get();
        if (state.restaurantId && state.restaurantId !== restaurantId && state.lines.length > 0) {
          return { ok: false, conflict: true };
        }
        const existing = state.lines.find((l) => l.menu_item_id === line.menu_item_id);
        const next = existing
          ? state.lines.map((l) =>
              l.menu_item_id === line.menu_item_id ? { ...l, quantity: l.quantity + qty } : l,
            )
          : [...state.lines, { ...line, quantity: qty }];
        set({ restaurantId, restaurantName, lines: next });
        return { ok: true };
      },
      setQuantity: (menuItemId, qty) => {
        const lines = get().lines
          .map((l) => (l.menu_item_id === menuItemId ? { ...l, quantity: qty } : l))
          .filter((l) => l.quantity > 0);
        set({ lines });
        if (lines.length === 0) set({ restaurantId: null, restaurantName: null });
      },
      remove: (menuItemId) => {
        const lines = get().lines.filter((l) => l.menu_item_id !== menuItemId);
        set({ lines });
        if (lines.length === 0) set({ restaurantId: null, restaurantName: null });
      },
      clear: () => set({ restaurantId: null, restaurantName: null, lines: [] }),
      total: () => get().lines.reduce((s, l) => s + l.price * l.quantity, 0),
      itemCount: () => get().lines.reduce((s, l) => s + l.quantity, 0),
    }),
    { name: 'pm-restaurant-cart' },
  ),
);

